import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldError } from 'react-hook-form';
import { Box, Typography, Avatar, InputLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IFormTypes } from './Types';

interface ProfileImageUploadProps {
  control: Control<IFormTypes, any>;
  error?: FieldError;
  setValue: (name: 'profileImage', value: File | null) => void;
}

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({ control, error, setValue }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const validateFile = (file: File): string | null => {
    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type.toLowerCase())) {
      return 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.';
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 5MB limit. Please choose a smaller image.';
    }

    return null;
  };

  const handleFileChange = (file: File | null, onChange: (value: File | null) => void) => {
    // Clear previous preview and errors
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setValidationError(null);

    if (!file) {
      onChange(null);
      return;
    }

    // Validate file
    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      onChange(null);
      return;
    }

    // Create preview URL
    const blobUrl = URL.createObjectURL(file);
    setPreviewUrl(blobUrl);
    onChange(file);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <InputLabel
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          alignSelf: 'flex-start',
          color: '#171717',
          marginBottom: '12px',
        }}
      >
        Profile Image*
      </InputLabel>

      <Controller
        name="profileImage"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '20px',
              borderRadius: '16px',
              background: '#fff',
              boxShadow: '0 6px 40px rgba(208, 208, 208, 0.4)',
            }}
          >
            <input
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              id="profile-image-upload"
              type="file"
              onChange={e => {
                const file = e.target.files?.[0] || null;
                handleFileChange(file, onChange);
              }}
              data-testid="profile-image-input"
            />

            <label htmlFor="profile-image-upload" style={{ cursor: 'pointer' }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={previewUrl || undefined}
                  sx={{
                    width: 150,
                    height: 150,
                    bgcolor: '#f0f0f0',
                    border: '2px solid #e0e0e0',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                      borderColor: '#999',
                    },
                  }}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  data-testid="change-photo-button"
                >
                  <AddIcon sx={{ color: '#666' }} />
                </Box>
              </Box>
            </label>

            <Typography
              sx={{
                fontSize: '14px',
                color: '#666',
                textAlign: 'center',
              }}
            >
              {previewUrl ? 'Tap to change photo' : 'Tap to upload photo'}
            </Typography>

            <Typography
              sx={{
                fontSize: '12px',
                color: '#999',
                textAlign: 'center',
              }}
            >
              Allowed: JPEG, PNG, GIF, WebP (Max 5MB)
            </Typography>
          </Box>
        )}
      />

      {(validationError || error) && (
        <Typography
          sx={{
            fontSize: '12px',
            color: 'red',
            marginTop: '8px',
          }}
          data-testid="profile-image-error"
        >
          {validationError || error?.message}
        </Typography>
      )}
    </Box>
  );
};

export default ProfileImageUpload;

